import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../apis/subscription.api';
import { toast } from 'react-toastify';
import ExplorerPurchase from '../components/ExplorerPurchase';
import EnterprisePurchase from '../components/EnterprisePurchase';
// redux
import { setCurrentPage } from '../redux/app.slice';
import { renewalKind } from '../processes/subscriptionGate';

const Subscribe = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { idToken } = useSelector((state) => state.user.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isOrgLeader, setIsOrgLeader] = useState(false);
  const [orgLeaderEmail, setOrgLeaderEmail] = useState('');
  const [isReady, setIsReady] = useState(false);
  const { subscriptionIssueType, subscriptionInfo } = useSelector((state) => state.app);
  const [showExplorerForm, setShowExplorerForm] = useState(false);
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);
  
  useEffect(() => {
    dispatch(setCurrentPage('subscribe'));
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      console.log('Subscribe: Loading with organization:', user.organization);
      console.log('Subscribe: Current user email:', user.email);

      if (!user.organization || !idToken) {
        console.error('Subscribe: Missing required data:', { 
          organization: user.organization, 
          token: idToken 
        });
        setError('Missing required data');
        setLoading(false);
        return;
      }

      try {
        console.log('Subscribe: Fetching subscription details for org:', user.organization);
        const response = await subscriptionApi.getSubscriptionDetails(
          idToken,
          user.organization
        );
        console.log('Subscribe: Subscription details:', response);

        if (!response.success || !response.subscription) {
          throw new Error('Invalid subscription response');
        }

        setSubscriptionData(response);
        setOrgLeaderEmail(response.subscription.orgLeader.email);
        console.log('Subscribe: Org leader email:', response.subscription.orgLeader.email);
        console.log('Subscribe: Current user email:', user.email);
        // Fix: Simplified leader check to just compare emails
        const isLeader = user.email === response.subscription.orgLeader.email;
        
        console.log('Subscribe: Leader check:', { 
          currentUserEmail: user.email, 
          leaderEmail: response.subscription.orgLeader.email,
          isLeader 
        });

        setIsOrgLeader(isLeader);

      } catch (err) {
        console.error('Subscribe: Error loading subscription details:', err);
        setError('Failed to load subscription details');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user.organization, idToken, user.email]);

  // Handler functions
  const handleExplorerClick = () => {
    setShowExplorerForm(true);
    setShowEnterpriseForm(false);
  };

  const handleEnterpriseClick = () => {
    setShowEnterpriseForm(true);
    setShowExplorerForm(false);
  };

  /*
   * How this org renews. Explorer renews through the Explorer checkout (the
   * renew session), Enterprise through an inquiry, and a trial does not renew
   * at all — a trial becomes a customer as a NEW organisation, so its leader is
   * offered a conversation, never a checkout that would turn the trial org
   * itself into a paid one.
   */
  const kind = renewalKind({
    orgType: subscriptionInfo?.orgType,
    planType: subscriptionData?.subscription?.details?.planType,
    orgName: subscriptionData?.subscription?.details?.orgName || user.organization,
  });

  const handleBackToPlans = () => {
    setShowExplorerForm(false);
    setShowEnterpriseForm(false);
  };
  

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <p>Loading subscription details...</p>
      </div>
    );
  }

  // Show purchase forms if selected
  if (showExplorerForm) {
    return (
      <div className="max-w-4xl mx-auto mt-2">
        <button
          onClick={handleBackToPlans}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Plans
        </button>
        <ExplorerPurchase
          defaultSeats={subscriptionData?.subscription?.features?.maxUsers || 1}
          activeSeats={subscriptionData?.users?.active?.count || 0}
        />
      </div>
    );
  }

  if (showEnterpriseForm) {
    return (
      <div className="max-w-4xl mx-auto mt-2">
        <button
          onClick={handleBackToPlans}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Plans
        </button>
        <EnterprisePurchase 
          isRenewal={kind !== 'trial'}
          subscriptionData={subscriptionData}
        />
      </div>
    );
  }

  // Show message for non-leaders
  if (!isOrgLeader) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-4">{subscriptionIssueType === 'org' && kind === 'trial' ? 'Trial Ended' : 'Subscription Expired'}</h1>
        <p className="text-gray-700 mb-4">
          {subscriptionIssueType === 'org' 
            ? (kind === 'trial'
                ? `Your organization's trial has ended. Please contact your organization leader${orgLeaderEmail ? ` (${orgLeaderEmail})` : ''} or support@lavenirai.com.`
                : `Your organization's subscription has expired. Please contact your organization leader${orgLeaderEmail ? ` (${orgLeaderEmail})` : ''} to renew the subscription, or support@lavenirai.com.`)
            : `Your user subscription is inactive. Please contact your organization leader${orgLeaderEmail ? ` (${orgLeaderEmail})` : ''} to reactivate your access.  Contact support@lavenirai.com to leave this organization.`
          }
        </p>
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => navigate('/login')}
        >
          Return to Login
        </button>
      </div>
    );
  }

  // Modify the "BUY NOW" and "CONTACT US" buttons in the plans section
  return (
    <div className="max-w-4xl mx-auto mt-2 p-8 bg-white rounded-lg shadow-md">
      <h1 className="text-3xl font-semibold mb-8 text-center">Subscription Management</h1>
      
      {/* Organization Info */}
      <div className="mb-8">
        <h2 className="text-2xl font-medium mb-6 text-center">Organization Details</h2>
        <div className="grid grid-cols-2 gap-x-32 gap-y-4 max-w-xl mx-auto text-center">
          <div>
            <p className="text-gray-500 text-sm mb-1">Organization</p>
            <p className="text-gray-900 font-medium text-lg">{subscriptionData?.subscription?.details?.orgName}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Current Plan</p>
            <p className="text-gray-900 font-medium text-lg capitalize">
              {subscriptionData?.subscription?.details?.planType || 'No active plan'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Status</p>
            <p className="text-red-600 font-medium text-lg capitalize">
              {subscriptionData?.subscription?.details?.status}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Expiration Date</p>
            <p className="text-gray-900 font-medium text-lg">
              {subscriptionData?.subscription?.details?.endDate 
                ? new Date(subscriptionData.subscription.details.endDate).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Users</p>
            <p className="text-gray-900 font-medium text-lg">
              {`${subscriptionData?.users?.active?.count || 0} active / ${subscriptionData?.subscription?.features?.maxUsers || 0} total`}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Billing Interval</p>
            <p className="text-gray-900 font-medium text-lg capitalize">
              {subscriptionData?.subscription?.billing?.interval || 'N/A'}
            </p>
          </div>
        </div>
      </div>
  
      {/* A trial is not renewed: its leader is pointed at a conversation instead. */}
      {kind === 'trial' && subscriptionIssueType === 'org' && (
        <div className="text-center">
          <h2 className="text-2xl font-medium mb-4">Your trial has ended</h2>
          <p className="text-gray-600 mb-6">
            We'd love to help you continue with LavenirAI. Tell us about your team and we'll set up your subscription.
          </p>
          <button
            className="bg-[#10193A] text-white py-3 px-8 rounded-md hover:bg-opacity-90 transition-colors font-medium"
            onClick={handleEnterpriseClick}
          >
            CONTACT US
          </button>
        </div>
      )}

      {/* Available Plans */}
      {!(kind === 'trial' && subscriptionIssueType === 'org') && (
      <div>
        <h2 className="text-2xl font-medium mb-6 text-center">{subscriptionIssueType === 'org' ? 'Renew Your Subscription' : 'Available Plans'}</h2>
        <div className="flex justify-center gap-8">
          {/* Explorer Plan — renews an Explorer org; never offered to an Enterprise
              org, whose renewal would otherwise switch it to the Explorer plan. */}
          {kind !== 'enterprise' && (
          <div className="border border-gray-200 rounded-lg p-8 w-[450px] bg-white shadow-sm flex flex-col">
            <h3 className="text-2xl font-semibold mb-6">Explorer (1-9 Seats)</h3>
            <ul className="space-y-5 mb-auto">
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>40+ chapters (40+ hours of learning content)</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>17 AI negotiation case study roleplays</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Ask Harini (AI assistant trained on procurement and negotiation)</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>AI Negotiation Plan generator (with AI consultation of plan)</span>
              </li>
            </ul>
            <div className="text-center mt-8">
              <p className="text-2xl font-bold mb-4">$2400/year/seat</p>
              <button 
                className="w-full bg-[#10193A] text-white py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors font-medium"
                onClick={handleExplorerClick}
              >
                {kind === 'explorer' ? 'RENEW' : 'BUY NOW'}
              </button>
            </div>
          </div>
          )}
  
          {/* Enterprise Plan */}
          <div className="border border-gray-200 rounded-lg p-8 w-[450px] bg-white shadow-sm flex flex-col">
            <h3 className="text-2xl font-semibold mb-6">Enterprise (10+ Seats)</h3>
            <ul className="space-y-5 mb-auto">
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Everything in Explorer</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Custom case studies</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Seat management support</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Bulk discounts, more seats</span>
              </li>
              <li className="flex items-start text-gray-600">
                <svg className="w-6 h-6 mr-3 text-blue-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Custom contracts and development</span>
              </li>
            </ul>
            <div className="text-center mt-8">
              <p className="text-2xl font-bold mb-4">Request Enterprise Pricing</p>
              <button 
                className="w-full bg-[#10193A] text-white py-3 px-4 rounded-md hover:bg-opacity-90 transition-colors font-medium"
                onClick={handleEnterpriseClick}
              >
                {kind === 'enterprise' ? 'RENEW — CONTACT US' : 'CONTACT US'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Subscribe;
